import type { TypesenseSearchResult } from '~/utils/typesenseClient'

export interface NormalizedHit {
  objectID: string
  _highlightResult: Record<string, unknown>
}

/** Algolia-shaped envelope. Not the full @algolia/client-search SearchResponse
 * type (params/index/etc. aren't meaningful for Typesense) — callers only
 * ever read hits/nbHits/nbPages/facets/queryID/_highlightResult/objectID. */
export interface NormalizedSearchResponse<T> {
  hits: (T & NormalizedHit)[]
  nbHits: number
  page: number
  nbPages: number
  hitsPerPage: number
  facets: Record<string, Record<string, number>>
  queryID: null
  query: string
  exhaustiveNbHits: boolean
  processingTimeMS: number
}

export interface AdaptTypesenseSearchResponseOptions {
  query: string
  /** Typesense per_page used for the request, to derive nbPages. */
  perPage: number
}

const toFacets = (
  facetCounts: TypesenseSearchResult<unknown>['facet_counts']
): Record<string, Record<string, number>> => {
  if (!facetCounts) {
    return {}
  }

  return Object.fromEntries(
    facetCounts.map(({ field_name, counts }) => [
      field_name,
      Object.fromEntries(counts.map(({ value, count }) => [value, count])),
    ])
  )
}

/**
 * Typesense -> Algolia-shaped envelope.
 * Typesense `page` is 1-indexed, Algolia `page` is 0-indexed.
 *
 * T is intentionally unconstrained: every Typesense document has a system
 * `id: string` regardless of what the app-level hit type T declares (e.g.
 * SearchEngineEventItem.id is a numeric Algolia business field, unrelated to
 * Typesense's own id) — so `id` is read via a cast, not a generic bound.
 */
export const adaptTypesenseSearchResponse = <T extends { id: string | number }>(
  typesenseResult: TypesenseSearchResult<T>,
  { query, perPage }: AdaptTypesenseSearchResponseOptions
): NormalizedSearchResponse<T> => {
  const hits = (typesenseResult.hits ?? []).map(({ document, highlight }) => ({
    ...document,
    objectID: String(document.id),
    _highlightResult: highlight ?? {},
  }))

  return {
    hits,
    nbHits: typesenseResult.found,
    page: Math.max(typesenseResult.page - 1, 0),
    nbPages: Math.ceil(typesenseResult.found / perPage),
    hitsPerPage: perPage,
    facets: toFacets(typesenseResult.facet_counts),
    queryID: null,
    query,
    exhaustiveNbHits: true,
    processingTimeMS: 0,
  }
}
